import {Component, OnDestroy, OnInit} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {MatBottomSheet} from '@angular/material/bottom-sheet';
import {combineLatest, firstValueFrom, Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import {ChecklistPdfZipFailure, ChecklistPdfZipJob, ChecklistPdfZipObject} from 'src/app/models/ChecklistPdfZip';
import {ConfirmComponent} from 'src/app/components/common/bottom-sheet/confirm/confirm.component';
import {User} from 'src/app/models/User';
import {FileService} from 'src/app/services/file.service';
import {SharedService} from 'src/app/services/shared.service';

@Component({
  selector: 'app-pdf-exports',
  templateUrl: './pdf-exports.component.html',
  styleUrls: ['./pdf-exports.component.css'],
})
export class PdfExportsComponent implements OnInit, OnDestroy {
  projectId: string;
  user: User;
  jobs: ChecklistPdfZipJob[] = [];
  report: ChecklistPdfZipJob = null;
  failures: ChecklistPdfZipFailure[] = [];
  objects: ChecklistPdfZipObject[] = [];
  objectCount = 0;
  nextObjectOffset: number | null = null;
  loadingObjects = false;
  markingDelivery = false;
  nextJobId: string | null = null;
  nextOffset: number | null = null;
  loading = true;
  loadingHistory = false;
  loadingFailures = false;
  requestPending = false;
  error = '';
  private selectedId: string;
  private routeKey = '';
  private version = 0;
  private pollTimer: ReturnType<typeof setTimeout>;
  private readonly destroyed = new Subject<void>();

  constructor(private route: ActivatedRoute, private router: Router,
              private sharedservice: SharedService, private fileservice: FileService, private bottomSheet: MatBottomSheet) {}

  ngOnInit() {
    combineLatest([this.route.paramMap, this.route.queryParamMap, this.sharedservice.user])
      .pipe(takeUntil(this.destroyed))
      .subscribe(([params, query, user]) => {
        if (!user) { return; }
        const projectId = params.get('projectid');
        const id = query.get('export');
        const key = JSON.stringify([projectId, id, user.id, user.superAdmin]);
        if (key === this.routeKey) { return; }
        this.routeKey = key;
        this.user = user;
        this.projectId = projectId;
        void this.loadExports(id);
      });
  }

  ngOnDestroy() {
    this.version++;
    clearTimeout(this.pollTimer);
    this.destroyed.next();
    this.destroyed.complete();
  }

  get working(): boolean {
    return !!this.report && ['PREPARING', 'QUEUED', 'CHECKING', 'REGENERATING', 'ARCHIVING'].includes(this.report.status);
  }

  get canRecover(): boolean {
    return !this.loading && !this.requestPending && this.report?.status === 'AWAITING_DECISION';
  }

  get canMarkDelivered(): boolean {
    return !this.loading && !this.loadingObjects && !this.requestPending && this.user?.superAdmin &&
      this.report?.status === 'COMPLETED' && this.report.archived > 0 &&
      (!this.report.delivery?.completedAt || !!this.report.delivery.retryable);
  }

  get progress(): number {
    const job = this.report;
    const total = job.status === 'ARCHIVING' ? job.total - job.failed : job.total;
    const done = job.status === 'ARCHIVING' ? job.archived : job.checked;
    return total ? Math.min(100, done / total * 100) : 0;
  }

  statusLabel(status: string): string {
    return ({PREPARING: 'Forbereder', QUEUED: 'I kø', CHECKING: 'Kontrollerer PDF-er',
      REGENERATING: 'Genererer PDF-er', ARCHIVING: 'Lager ZIP', AWAITING_DECISION: 'Trenger oppfølging',
      COMPLETED: 'Klar til nedlasting', FAILED: 'Eksporten feilet'})[status] || status;
  }

  async openExport(id: string) {
    await this.router.navigate([], {relativeTo: this.route, queryParams: {export: id}});
  }

  async loadExports(id = this.selectedId) {
    const version = ++this.version;
    clearTimeout(this.pollTimer);
    this.selectedId = id;
    this.jobs = []; this.report = null; this.failures = [];
    this.objects = []; this.objectCount = 0; this.nextObjectOffset = null;
    this.loadingObjects = false; this.markingDelivery = false;
    this.nextJobId = null; this.nextOffset = null;
    this.loading = true; this.requestPending = false;
    this.loadingHistory = false; this.loadingFailures = false; this.error = '';
    if (!this.user?.superAdmin) { this.loading = false; return; }
    try {
      const result = await this.fileservice.checklistPdfZip({action: 'list', projectId: this.projectId});
      if (version !== this.version) { return; }
      this.jobs = result.jobs;
      this.nextJobId = result.nextJobId;
      this.selectedId = id || this.jobs[0]?.id;
      if (this.selectedId) {
        await this.refreshExport(version);
        if (version === this.version && this.report) { await this.loadObjects(); }
      }
    } catch (error) { if (version === this.version) { this.showError(error); } }
    finally { if (version === this.version) { this.loading = false; } }
  }

  private async refreshExport(version: number) {
    clearTimeout(this.pollTimer);
    try {
      const result = await this.fileservice.checklistPdfZip({action: 'status', id: this.selectedId});
      if (version !== this.version) { return; }
      if (result.job.projectId !== this.projectId) { throw new Error('Eksporten tilhører et annet prosjekt.'); }
      this.report = result.job;
      this.failures = result.failures;
      this.nextOffset = result.nextOffset;
      this.jobs = this.jobs.map(job => job.id === this.report.id ? this.report : job);
      if (!this.jobs.some(job => job.id === this.report.id)) { this.jobs.push(this.report); }
    } catch (error) { if (version === this.version) { this.showError(error); } }
    finally {
      if (version === this.version && (this.working || this.report?.status === 'AWAITING_DECISION')) {
        this.pollTimer = setTimeout(() => this.refreshExport(version), this.working ? 3000 : 15000);
      }
    }
  }

  async loadOlderExports() {
    if (!this.nextJobId || this.loadingHistory) { return; }
    const version = this.version;
    this.loadingHistory = true;
    try {
      const result = await this.fileservice.checklistPdfZip({action: 'list', projectId: this.projectId, beforeId: this.nextJobId});
      if (version !== this.version) { return; }
      this.jobs.push(...result.jobs.filter(job => !this.jobs.some(existing => existing.id === job.id)));
      this.nextJobId = result.nextJobId;
    } catch (error) { if (version === this.version) { this.showError(error); } }
    finally { if (version === this.version) { this.loadingHistory = false; } }
  }

  async loadMoreFailures() {
    if (!this.report || this.nextOffset === null || this.loadingFailures) { return; }
    const version = this.version;
    this.loadingFailures = true;
    try {
      const result = await this.fileservice.checklistPdfZip({action: 'status', id: this.report.id, offset: this.nextOffset});
      if (version !== this.version) { return; }
      this.failures.push(...result.failures.filter(item => !this.failures.some(existing => existing.path === item.path)));
      this.nextOffset = result.nextOffset;
    } catch (error) { if (version === this.version) { this.showError(error); } }
    finally { if (version === this.version) { this.loadingFailures = false; } }
  }

  async loadObjects(append = false) {
    if (!this.report || this.loadingObjects || this.requestPending || (append && this.nextObjectOffset === null)) { return; }
    const version = this.version;
    this.loadingObjects = true;
    try {
      const result = await this.fileservice.checklistPdfZip({action: 'objects', id: this.report.id,
        offset: append ? this.nextObjectOffset : 0});
      if (version !== this.version) { return; }
      this.objects = append ? this.objects.concat(result.objects) : result.objects;
      this.objectCount = result.objectCount;
      this.nextObjectOffset = result.nextObjectOffset;
    } catch (error) { if (version === this.version) { this.showError(error); } }
    finally { if (version === this.version) { this.loadingObjects = false; } }
  }

  async markDelivered() {
    if (!this.canMarkDelivered) { return; }
    const version = this.version;
    const sheet = this.bottomSheet.open(ConfirmComponent, {backdropClass: 'blur-dialog-backdrop', data: {
      title: 'Merk som sendt til kunde?',
      text: 'Bekreft at PDF-ene i denne ZIP-filen er sendt til kunden.',
      multiLine: ['Signerte sjekklister med alle sine PDF-er i eksporten merkes som levert.',
        'Sjekklister med utelatte PDF-er eller PDF-er utenfor utvalget blir hoppet over.'],
      yes: {name: 'Merk som sendt', icon: 'done_all'}, no: {name: 'Avbryt', icon: 'close'},
    }});
    const confirmed = await firstValueFrom(sheet.afterDismissed().pipe(takeUntil(this.destroyed)), {defaultValue: false});
    if (confirmed && version === this.version && this.canMarkDelivered) {
      await this.action('markDelivered', {confirmDelivered: true});
    }
  }

  private async action(action: string, options: object = {}) {
    if (!this.report || this.loading || this.requestPending || !this.user?.superAdmin) { return; }
    // Invalidate an in-flight poll before submitting a change to this export.
    const version = ++this.version;
    clearTimeout(this.pollTimer);
    this.loadingHistory = false; this.loadingFailures = false; this.loadingObjects = false;
    this.requestPending = true; this.error = '';
    this.markingDelivery = action === 'markDelivered';
    try {
      do {
        const result = await this.fileservice.checklistPdfZip({action, id: this.report.id, updatedAt: this.report.updatedAt, ...options});
        if (version !== this.version) { return; }
        this.report = result.job;
      } while (this.markingDelivery && this.report.delivery && !this.report.delivery.completedAt);
    } catch (error) { if (version === this.version) { this.showError(error); } }
    finally {
      if (version === this.version) {
        this.requestPending = false; this.markingDelivery = false;
        await this.refreshExport(version);
      }
    }
  }

  async regenerate(path?: string) {
    if (!this.canRecover || !this.user?.superAdmin) { return; }
    if (!window.confirm('Regenerering erstatter de valgte lagrede PDF-eksportene med dagens sjekklisteinnhold og prosjektets bildekvalitet. Fortsette?')) { return; }
    await this.action('regenerate', path ? {path} : {});
  }

  async retry() { await this.action('retry'); }

  async skipFailures() {
    if (!this.canRecover) { return; }
    if (!window.confirm(`ZIP-filen vil mangle ${this.report.failed} PDF-er. Fortsette med en ufullstendig eksport og feilrapport?`)) { return; }
    await this.action('skip', {confirmIncomplete: true});
  }

  async download(report = false) {
    if (!this.report || this.requestPending) { return; }
    const version = this.version;
    this.requestPending = true;
    try {
      const result = await this.fileservice.checklistPdfZip({action: report ? 'report' : 'download', id: this.report.id});
      if (version !== this.version) { return; }
      // Let the browser download manager stream the signed response without a Blob.
      const link = document.createElement('a');
      link.href = result.url; link.rel = 'noopener';
      document.body.appendChild(link); link.click(); link.remove();
    } catch (error) { if (version === this.version) { this.showError(error); } }
    finally { if (version === this.version) { this.requestPending = false; } }
  }

  private showError(error: any) {
    console.error('PDF export failed', error);
    this.error = error?.message === 'INTERNAL' ? 'Kunne ikke hente eksporten. Prøv igjen.' : error?.message || String(error);
  }
}
